import type { CSSProperties, MouseEvent, ReactNode } from 'react'
import {
  SHELL_COLUMN_FOOTER_CLASS,
  SHELL_COLUMN_HEADER_CLASS,
  SHELL_ICON_RAIL_BUTTON_ACTIVE_CLASS,
  SHELL_ICON_RAIL_BUTTON_BASE_CLASS,
  SHELL_ICON_RAIL_BUTTON_DIMMED_CLASS,
  SHELL_ICON_RAIL_BUTTON_IDLE_CLASS,
  SHELL_ICON_RAIL_BUTTON_SUGGESTED_CLASS,
  SHELL_ICON_RAIL_CLASS,
} from '@/lib/shell-chrome'
import { cn } from '@/lib/utils'

export type ShellIconRailProps = {
  /** `ShellPanelToggle side="left" placement="slot"`. Always the top cap. */
  toggle: ReactNode
  /** Workspace scope, directly under the toggle. */
  scope?: ReactNode
  /** Mode buttons (`ShellIconRailButton`), grouped however the caller needs. */
  children: ReactNode
  /** Settings. Sits on the column footer line. */
  footer?: ReactNode
  className?: string
  style?: CSSProperties
  testId?: string
}

/**
 * The 48px left column: toggle cap, scope, mode buttons, Settings footer.
 * Gallery: /?test=maater-ui#panel-collapse
 */
export function ShellIconRail({
  toggle,
  scope,
  children,
  footer,
  className,
  style,
  testId,
}: ShellIconRailProps) {
  return (
    <div
      data-testid={testId}
      className={cn(
        SHELL_ICON_RAIL_CLASS,
        'group/icon-rail flex h-full flex-none flex-col items-center border-r border-border bg-sidebar-accent',
        className,
      )}
      style={style}
    >
      {toggle}
      {scope}
      <div className="flex w-full flex-col items-center gap-1 pt-1">{children}</div>
      <div className="flex-1" />
      {footer}
    </div>
  )
}

export type ShellIconRailButtonTone = 'idle' | 'active' | 'dimmed' | 'suggested'

const TONE_CLASS: Record<ShellIconRailButtonTone, string> = {
  idle: SHELL_ICON_RAIL_BUTTON_IDLE_CLASS,
  active: SHELL_ICON_RAIL_BUTTON_ACTIVE_CLASS,
  dimmed: SHELL_ICON_RAIL_BUTTON_DIMMED_CLASS,
  suggested: SHELL_ICON_RAIL_BUTTON_SUGGESTED_CLASS,
}

/** One mode button. Callers pick the tone; they do not restyle the button. */
export function ShellIconRailButton({
  icon,
  label,
  tone = 'idle',
  onClick,
  testId,
  className,
  children,
}: {
  icon: ReactNode
  label: string
  tone?: ShellIconRailButtonTone
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void
  testId?: string
  /** Extra motion only (pulse). Not colors. */
  className?: string
  /** Badges. */
  children?: ReactNode
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-current={tone === 'active' ? 'page' : undefined}
      data-testid={testId}
      className={cn(SHELL_ICON_RAIL_BUTTON_BASE_CLASS, TONE_CLASS[tone], className)}
      onClick={onClick}
    >
      {icon}
      {children}
    </button>
  )
}

/**
 * Full-width cap on the header line (scope) or footer line (Settings).
 * Same quiet hover as the mode buttons, no ring.
 */
export function ShellIconRailCap({
  line,
  label,
  onClick,
  testId,
  className,
  children,
}: {
  line: 'header' | 'footer'
  label: string
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void
  testId?: string
  className?: string
  children: ReactNode
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      data-testid={testId}
      className={cn(
        // The toggle above owns the header hairline; the footer draws its own.
        line === 'header' ? SHELL_COLUMN_HEADER_CLASS : cn(SHELL_COLUMN_FOOTER_CLASS, 'border-t'),
        'flex w-full items-center justify-center border-border',
        'text-sidebar-foreground/45 transition-[color,background-color] duration-150',
        'hover:bg-primary/10 hover:text-sidebar-foreground',
        className,
      )}
      onClick={onClick}
    >
      {children}
    </button>
  )
}
